/**
 * WHAT: CSRF Token Service
 * WHY: Handle CSRF token management for forms
 * HOW: Fetch and store CSRF tokens
 * IMPORTANCE: Security against CSRF attacks
 */

import { CONFIG } from '../config/config.js';

class CSRFService {
    constructor() {
        this.token = null;
        this.lastFetch = null;
        this.cacheDuration = 5 * 60 * 1000; // 5 minutes
    }

    /**
     * WHAT: Get CSRF token
     * WHY: Ensure forms have valid CSRF tokens
     * HOW: Fetch from API with caching
     * IMPORTANCE: Security
     */
    async getToken() {
        const now = Date.now();
        
        // Return cached token if still valid
        if (this.token && this.lastFetch && (now - this.lastFetch) < this.cacheDuration) {
            return this.token;
        }

        try {
            const response = await fetch(CONFIG.getApiUrl('csrf-token'), {
                method: 'GET',
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.success && data.data.csrfToken) {
                this.token = data.data.csrfToken;
                this.lastFetch = now;
                return this.token;
            } else {
                throw new Error('Invalid CSRF token response');
            }
        } catch (error) {
            console.error('Error fetching CSRF token:', error);
            throw error;
        }
    }

    /**
     * WHAT: Add CSRF token to form data
     * WHY: Include CSRF protection in form submissions
     * HOW: Add token to FormData or object
     * IMPORTANCE: Security
     */
    async addTokenToFormData(formData) {
        const token = await this.getToken();
        formData.append('_csrf', token);
        return formData;
    }

    /**
     * WHAT: Add CSRF token to JSON data
     * WHY: Include CSRF protection in JSON requests
     * HOW: Add token to request body
     * IMPORTANCE: Security
     */
    async addTokenToJson(data) {
        const token = await this.getToken();
        return {
            ...data,
            _csrf: token
        };
    }

    /**
     * WHAT: Add CSRF token to headers
     * WHY: Include CSRF protection in custom headers
     * HOW: Add token to request headers
     * IMPORTANCE: Security
     */
    async addTokenToHeaders(headers = {}) {
        const token = await this.getToken();
        return {
            ...headers,
            'X-CSRF-Token': token
        };
    }

    /**
     * WHAT: Clear cached token
     * WHY: Force refresh of CSRF token
     * HOW: Reset cache
     * IMPORTANCE: Token refresh
     */
    clearToken() {
        this.token = null;
        this.lastFetch = null;
    }
}

// Export singleton instance
export default new CSRFService();
